/**
 * Comment Service
 *
 * Domain layer for content comments. Datasource-agnostic: all Firestore
 * access is delegated to firestoreService, so this service can be swapped
 * onto a different backend (e.g. cloud functions) without touching the
 * domain-level orchestration here.
 *
 * Mirrors Flutter's ContentReactionsDataSource comment-related methods:
 *   - getComments, getComment, getNotificationComments, addComment, likeComment
 */

import type { Unsubscribe } from 'firebase/firestore';
import type { PageCursor } from '@/data/types/json';
import { firestoreService } from '@/data/services/firestoreService';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { CommentRemapper } from '@/domain/remappers/comment/commentRemapper';
import { CommentLikeRemapper } from '@/domain/remappers/commentLike/commentLikeRemapper';
import { NotificationInfoRemapper } from '@/domain/remappers/notificationInfo/notificationInfoRemapper';
import type { CommentEntity } from '@/domain/entities/comment/commentEntity';
import type { NotificationInfoDto } from '@/data/dtos/info/notificationInfoDto';
import type { CommentLikeDto } from '@/data/dtos/commentLike/commentLikeDto';

export interface CommentsPage {
  comments: CommentEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class CommentService {
  private static instance: CommentService;

  public static getInstance(): CommentService {
    if (!CommentService.instance) {
      CommentService.instance = new CommentService();
    }
    return CommentService.instance;
  }

  /**
   * Paginated fetch of comments (root if parentId is null, else children).
   */
  async fetchComments(
    mediaPath: string,
    parentId: string | null,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<CommentsPage> {
    const page = await firestoreService.getComments(mediaPath, parentId, limitCount, cursor);
    return {
      comments: page.items.map(data => CommentRemapper.toEntity(data)),
      hasMore: page.hasMore,
      cursor: page.cursor,
    };
  }

  /**
   * Fetch a single comment by its id across all `comments` subcollections.
   */
  async fetchCommentById(id: string): Promise<CommentEntity | null> {
    const data = await firestoreService.getCommentById(id);
    return data ? CommentRemapper.toEntity(data) : null;
  }

  /**
   * Fetch multiple comments whose id is in the provided list.
   * Used for the CommentInfo deep-link (notification tap).
   */
  async fetchCommentsByIds(ids: string[]): Promise<CommentEntity[]> {
    const items = await firestoreService.getCommentsByIds(ids);
    return items.map(data => CommentRemapper.toEntity(data));
  }

  /**
   * Subscribe to a single comment by id in real time.
   * Mirrors Flutter's _subscribeToContentReply usage.
   */
  subscribeToComment(id: string, onChange: (comment: CommentEntity | null) => void): Unsubscribe {
    return firestoreService.subscribeToCommentById(
      id,
      data => onChange(data ? CommentRemapper.toEntity(data) : null),
      error => appLogger.error('subscribeToComment error', 'CommentService.subscribeToComment', { error: error }),
    );
  }

  /**
   * Add a comment. Remaps domain entities to raw maps and delegates the
   * write to the data layer.
   */
  async addComment(
    comment: CommentEntity,
    authorUserId: string,
    notification: NotificationInfoDto | null,
    commentReplyPopularity: number,
  ): Promise<boolean> {
    return firestoreService.addComment({
      commentId: comment.id,
      mediaPath: comment.mediaPath,
      parentId: comment.parentId ?? null,
      commentToReplyId: comment.commentToReply?.replyId ?? null,
      authorUserId,
      commentFirestoreMap: CommentRemapper.toJson(CommentRemapper.toDto(comment)),
      notification:
        notification && notification.usersInfo.peerUserId
          ? {
              peerUserId: notification.usersInfo.peerUserId,
              infoFirestoreMap: NotificationInfoRemapper.toJson(notification),
            }
          : null,
      commentReplyPopularity,
    });
  }

  /**
   * Like or unlike a comment.
   */
  async likeComment(
    like: CommentLikeDto,
    notification: NotificationInfoDto | null,
    commentLikePopularity: number,
  ): Promise<boolean> {
    return firestoreService.likeComment({
      commentId: like.commentId,
      userId: like.userId,
      parentCommentId: like.parentCommentId ?? null,
      liked: like.liked,
      likeFirestoreMap: CommentLikeRemapper.toJson(like),
      notification:
        notification && notification.usersInfo.peerUserId
          ? {
              peerUserId: notification.usersInfo.peerUserId,
              infoFirestoreMap: NotificationInfoRemapper.toJson(notification),
            }
          : null,
      commentLikePopularity,
    });
  }
}

export const commentService = CommentService.getInstance();
